import { FormErrors, FormOnSubmit, FormProps, FormValidate, FormValues } from '@qfa/forms';
import { FieldConfig } from '@kinetic-ui/data-layer';
import { TFunction } from 'i18next';
import { ComponentType, ExoticComponent, useCallback, useMemo, useState } from 'react';
import { SetState } from 'types/props';

export interface UseFormOptions<TFormValues extends FormValues> {
  defaultValues?: Partial<TFormValues>;
  validate?: FormValidate<Partial<TFormValues>>;
  fields: FieldConfig[];
  onSubmit?: FormOnSubmit;
  readOnly?: boolean;
  onChange?: (
    newValues: Partial<TFormValues>,
    changedFields: (keyof TFormValues)[]
  ) => Partial<TFormValues>;
  externalComponents?: {
    [key: string]: ComponentType<any> | ExoticComponent;
  };
  tErrors?: TFunction;
}

export interface UseFormData<TFormValues extends FormValues> {
  props: FormProps<TFormValues>;
  values: [
    values: Partial<TFormValues>,
    setValues: SetState<Partial<TFormValues>>,
    reset: (newValues?: Partial<TFormValues>) => void
  ];
  errors: [
    hasErrors: boolean,
    errors: FormErrors<TFormValues>,
    setErrors: SetState<FormErrors<TFormValues>>
  ];
  dirty: [
    isDirty: boolean,
    dirtyFields: string[],
    setDirtyFields: SetState<string[]>,
    helpers: {
      setFieldsDirty: (...fieldNames: (keyof TFormValues)[]) => void;
      setFieldsClean: (...fieldNames: (keyof TFormValues)[]) => void;
    }
  ];
  isSaveDisabled: boolean;
}

const emptyDefaultValues = {};
export const useForm = <TFormValues extends FormValues>(
  options: UseFormOptions<TFormValues>
): UseFormData<TFormValues> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const {
    defaultValues = emptyDefaultValues,
    validate,
    fields,
    onSubmit,
    readOnly,
    onChange: onChangeProp,
    externalComponents,
    tErrors,
  } = options;

  const [values, setValues] = useState<Partial<TFormValues>>(defaultValues);
  const [errors, setErrors] = useState<FormErrors<TFormValues>>({});
  const [dirtyFields, setDirtyFields] = useState<string[]>([]);

  const setFieldsDirty = useCallback((...fieldNames: (keyof TFormValues)[]) => {
    setDirtyFields((prev) => Array.from(new Set([...prev, ...(fieldNames as string[])])));
  }, []);
  const setFieldsClean = useCallback((...fieldNames: (keyof TFormValues)[]) => {
    setDirtyFields((prev) =>
      prev.filter((field) => !fieldNames.includes(field as keyof TFormValues))
    );
  }, []);

  const onChange = useCallback(
    (newValues, changedFields: (keyof TFormValues)[], newDirtyFields: string[]) => {
      setValues((prev) => {
        let updated: Partial<TFormValues> = { ...prev };
        if (changedFields.length) {
          changedFields.forEach((changedField) => {
            updated[changedField] = newValues[changedField];
          });
          if (onChangeProp) {
            updated = onChangeProp(updated, changedFields);
          }
        } else {
          updated = { ...newValues };
        }

        return updated;
      });
      setDirtyFields(newDirtyFields);
    },
    [onChangeProp]
  );

  const onErrors = useCallback((newErrors) => {
    setErrors(newErrors);
  }, []);

  const props = useMemo(() => {
    const baseProps: FormProps<TFormValues> = {
      onChange,
      onErrors,
      // type mismatch because api field config has some nullable fields, instead of undefined
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fields: fields as any,
      values,
      externalComponents,
      dirtyFields: dirtyFields as (keyof TFormValues)[],
    };

    // TS complains if we pass `undefined` to FormOwnProps for these values instead of omitting them.
    if (readOnly !== undefined) {
      baseProps.readOnly = readOnly;
    }
    if (onSubmit !== undefined) {
      baseProps.onSubmit = onSubmit;
    }
    if (validate !== undefined) {
      baseProps.validate = validate;
    }
    if (tErrors !== undefined) {
      baseProps.tErrors = tErrors;
    }

    return baseProps;
  }, [
    onChange,
    onErrors,
    fields,
    values,
    externalComponents,
    dirtyFields,
    readOnly,
    onSubmit,
    validate,
    tErrors,
  ]);

  const reset = useCallback(
    (newValues?: Partial<TFormValues>) => {
      onChange(newValues || defaultValues, [], []);
    },
    [defaultValues, onChange]
  );

  const hasErrors = Object.keys(errors).length > 0;
  const isDirty = dirtyFields.length > 0;

  return {
    props,
    values: [values, setValues, reset],
    errors: [hasErrors, errors, setErrors],
    dirty: [isDirty, dirtyFields, setDirtyFields, { setFieldsClean, setFieldsDirty }],
    isSaveDisabled: hasErrors || !isDirty,
  };
};
